import type { TtqSession, TtqSessionParticleEntry } from '../../services/ttqService';
import type { TtqibCanvasObject, TtqibLayer } from './types';
import { wholeObjectId } from './types';

export const TTQIB_CARD_WIDTH = 780;
export const TTQIB_COLUMN_GAP = 840;
export const TTQIB_LAYER_TOP = 80;
/** 源题与衍生 / 相邻衍生之间的纵向间距 */
export const TTQIB_STACK_GAP = 72;

/** 只布局颗粒整卡；段/图嵌在卡内跟随整卡，不占独立坐标 */
export function reflowLayerWholes(
  layer: TtqibLayer,
  objects: TtqibCanvasObject[],
): TtqibCanvasObject[] {
  const wholes = objects.filter((o) => o.layerId === layer.id && o.type === 'particle-whole');
  const nextWholeById = new Map<string, TtqibCanvasObject>();

  layer.sourceParticleIds.forEach((particleId, columnIndex) => {
    const whole = wholes.find((o) => o.particleId === particleId);
    if (!whole) return;
    const defaultX = 80 + columnIndex * TTQIB_COLUMN_GAP;
    nextWholeById.set(whole.id, {
      ...whole,
      layerId: layer.id,
      x: Number.isFinite(whole.x) ? whole.x : defaultX,
      y: Number.isFinite(whole.y) ? whole.y : TTQIB_LAYER_TOP,
      width: whole.width ?? TTQIB_CARD_WIDTH,
    });
  });

  let extra = layer.sourceParticleIds.length;
  for (const whole of wholes) {
    if (nextWholeById.has(whole.id)) continue;
    nextWholeById.set(whole.id, {
      ...whole,
      x: 80 + extra * TTQIB_COLUMN_GAP,
      y: TTQIB_LAYER_TOP,
      width: whole.width ?? TTQIB_CARD_WIDTH,
    });
    extra += 1;
  }

  return objects.map((object) => {
    if (object.type === 'particle-whole') {
      return nextWholeById.get(object.id) ?? object;
    }
    const parent = nextWholeById.get(wholeObjectId(object.particleId))
      ?? objects.find((item) => item.id === wholeObjectId(object.particleId));
    if (!parent) return object;
    return {
      ...object,
      layerId: parent.layerId,
      x: parent.x,
      y: parent.y,
      width: parent.width,
    };
  });
}

export function reflowAllObjects(
  _session: TtqSession,
  layers: TtqibLayer[],
  objects: TtqibCanvasObject[],
): TtqibCanvasObject[] {
  return layers.reduce((acc, layer) => reflowLayerWholes(layer, acc), objects);
}

export function reflowLayerObjects(
  _session: TtqSession,
  layer: TtqibLayer,
  objects: TtqibCanvasObject[],
): TtqibCanvasObject[] {
  return reflowLayerWholes(layer, objects);
}

export function detectParticleOverlap(objects: TtqibCanvasObject[]): boolean {
  const wholes = objects.filter((o) => o.type === 'particle-whole');
  for (let i = 0; i < wholes.length; i += 1) {
    for (let j = i + 1; j < wholes.length; j += 1) {
      const a = wholes[i];
      const b = wholes[j];
      if (a.layerId !== b.layerId) continue;
      const aw = a.width ?? TTQIB_CARD_WIDTH;
      const bw = b.width ?? TTQIB_CARD_WIDTH;
      const ah = 280;
      const bh = 280;
      if (a.x < b.x + bw && a.x + aw > b.x && a.y < b.y + bh && a.y + ah > b.y) {
        return true;
      }
    }
  }
  return false;
}

export function estimateWholeHeight(_entry?: TtqSessionParticleEntry): number {
  return 400;
}

export function estimateSegmentHeight(_text?: string): number {
  return 0;
}

export function estimateImageHeight(): number {
  return 0;
}
